import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Columns3, Check } from "lucide-react";

const DASH = "—";

const formatCurrency = (val) => {
  const num = typeof val === "string" ? parseFloat(val) : val;
  if (isNaN(num) || num == null) return DASH;
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(num);
};

const formatDate = (val) => {
  if (!val) return DASH;
  return new Date(val + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const formatDeadline = (val, hasTime) => {
  if (!val) return DASH;
  const d = new Date(val);
  if (isNaN(d.getTime())) return DASH;
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  if (!hasTime) {
    return `${months[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
  }
  let h = d.getUTCHours();
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  const min = String(d.getUTCMinutes()).padStart(2, "0");
  return `${months[d.getUTCMonth()]} ${d.getUTCDate()}, ${h}:${min} ${ampm}`;
};

const FIELDS = [
  { key: "offer_label", label: "Offer Label", format: (o) => o.offer_label || DASH },
  { key: "buyer_name", label: "Buyer Name", format: (o) => o.buyer_name || DASH },
  { key: "purchase_price", label: "Purchase Price", format: (o) => formatCurrency(o.purchase_price), highlight: true },
  { key: "earnest_money_deposit", label: "Earnest Money", format: (o) => formatCurrency(o.earnest_money_deposit) },
  { key: "financing_type", label: "Financing Type", format: (o) => o.financing_type || DASH },
  { key: "appraisal_gap_coverage", label: "Appraisal Gap", format: (o) => o.appraisal_gap_coverage || DASH },
  { key: "inspection_period_days", label: "Inspection Period", format: (o) => o.inspection_period_days != null ? `${o.inspection_period_days} days` : DASH },
  { key: "remedy_period_days", label: "Remedy Period", format: (o) => o.remedy_period_days != null ? `${o.remedy_period_days} days` : DASH },
  { key: "closing_date", label: "Closing Date", format: (o) => formatDate(o.closing_date) },
  { key: "offer_open_until", label: "Offer Open Until", format: (o) => formatDeadline(o.offer_open_until, !!o.offer_open_until_has_time) },
  { key: "home_warranty", label: "Home Warranty", format: (o) => o.home_warranty || DASH },
];

export const OfferComparisonView = ({ offers, onBack }) => {
  // Default: 2 most recent (offers are already sorted newest-first from the query)
  const [selectedIds, setSelectedIds] = useState(() => {
    return new Set(offers.slice(0, 2).map((o) => o.id));
  });

  const toggleOffer = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const selectedOffers = useMemo(
    () => offers.filter((o) => selectedIds.has(o.id)),
    [offers, selectedIds]
  );

  return (
    <Card className="border-border/60 shadow-soft" data-testid="offer-comparison-view">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={onBack} data-testid="comparison-back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <CardTitle className="font-heading text-base font-semibold flex items-center gap-2">
              <Columns3 className="w-4 h-4" />
              Compare Offers
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              {selectedOffers.length} of {offers.length} selected
            </p>
          </div>
        </div>

        {/* Selection chips */}
        <div className="flex items-center gap-1.5 overflow-x-auto pt-3 pb-1 scrollbar-none" data-testid="comparison-selection">
          {offers.map((offer) => {
            const isSelected = selectedIds.has(offer.id);
            return (
              <button
                key={offer.id}
                onClick={() => toggleOffer(offer.id)}
                className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  isSelected
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground"
                }`}
                data-testid={`comparison-chip-${offer.id}`}
              >
                {isSelected && <Check className="w-3 h-3" />}
                {offer.offer_label}
              </button>
            );
          })}
        </div>
      </CardHeader>

      <CardContent className="px-0 pb-4">
        {selectedOffers.length === 0 ? (
          <div className="text-center py-10 text-sm text-muted-foreground" data-testid="comparison-empty">
            Select at least one offer above to compare.
          </div>
        ) : (
          <div className="overflow-x-auto" data-testid="comparison-table-scroll">
            <table className="w-full text-sm" data-testid="comparison-table">
              <thead>
                <tr className="border-b border-border/60">
                  <th className="text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider px-4 py-2.5 sticky left-0 bg-card z-10 min-w-[140px]">
                    Field
                  </th>
                  {selectedOffers.map((offer) => (
                    <th key={offer.id} className="text-left text-xs font-semibold text-foreground px-4 py-2.5 min-w-[160px]" data-testid={`comparison-col-${offer.id}`}>
                      {offer.offer_label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {FIELDS.map((field, idx) => (
                  <tr
                    key={field.key}
                    className={`border-b border-border/30 ${idx % 2 === 0 ? "bg-muted/10" : ""}`}
                    data-testid={`comparison-row-${field.key}`}
                  >
                    <td className="text-xs font-medium text-muted-foreground px-4 py-2.5 sticky left-0 bg-inherit z-10 whitespace-nowrap">
                      {field.label}
                    </td>
                    {selectedOffers.map((offer) => {
                      const val = field.format(offer);
                      const isDash = val === DASH;
                      return (
                        <td
                          key={offer.id}
                          className={`px-4 py-2.5 whitespace-nowrap ${
                            isDash ? "text-muted-foreground/40" : field.highlight ? "font-semibold text-foreground" : "text-foreground"
                          }`}
                        >
                          {val}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
